import { Check, ChevronDown, Loader2, Search } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/shared/lib/cn';
import { achatar } from '@/shared/lib/texto';

export interface OpcaoCombo {
  valor: string;
  rotulo: string;
  /** Linha de apoio — o que desempata homônimos ("NINO" três vezes na lista). */
  detalhe?: string;
  /** Já escolhido: aparece marcado e não responde ao clique. */
  desabilitada?: boolean;
  /**
   * Cabeçalho da seção a que a opção pertence ("Espécies", "Grupos do
   * ambiente"). Opcional: sem ela a lista é plana, como sempre foi.
   *
   * ⚠️ **A ordem é a do array `opcoes`** — o combo agrupa opções VIZINHAS com
   * a mesma seção, não reordena. Quem monta a lista já a entrega na ordem em
   * que quer ler, e um agrupamento que reordenasse mudaria a lista de quem
   * passa `secao` em só metade das opções.
   */
  secao?: string;
}

interface Props {
  /** Vai no botão, para o `<Label htmlFor>` do `<Campo>` alcançar. */
  id?: string;
  /** `''` = nada escolhido. */
  valor: string;
  aoEscolher: (valor: string) => void;
  opcoes: OpcaoCombo[];
  /**
   * Rótulo do valor escolhido quando ele não está em `opcoes` — a busca no
   * servidor troca a lista inteira, e sem isto o gatilho ficaria em branco
   * depois de digitar outro termo.
   */
  rotuloSelecionado?: string;
  placeholder?: string;
  placeholderBusca?: string;
  vazio?: string;
  /** Chamado a cada tecla; quem consome decide se consulta o servidor. */
  aoBuscar?: (termo: string) => void;
  carregando?: boolean;
  desabilitado?: boolean;
  className?: string;
  'aria-label'?: string;
  /**
   * Aceita um valor que NÃO está na lista: o termo digitado vira uma opção
   * extra no topo ("Usar «...»"), e escolhê-la chama `aoEscolher` com o texto
   * cru.
   *
   * Existe para o nome do exame externo, que é sugerido pelo catálogo mas não
   * limitado a ele — um laudo de fora pode ser de um exame que a clínica não
   * oferece. Opcional porque, na maioria dos usos (paciente, analito, grupo), o
   * valor é um id e texto solto não significa nada.
   */
  permitirLivre?: boolean;
  /** Rótulo da opção de texto livre. Recebe o termo digitado. */
  rotuloLivre?: (termo: string) => string;
}

/** Constante, não default inline: identidade nova a cada render invalidaria o `useMemo`. */
const ROTULO_LIVRE_PADRAO = (termo: string) => `Usar “${termo}”`;

/**
 * Select com busca.
 *
 * ⚠️ **Não é o `<Select>` do Radix com um `<input>` dentro.** O Radix trata
 * tecla como typeahead da lista, então digitar no campo pularia a seleção junto
 * — daí o controle próprio.
 *
 * Agrupa por seção quando as opções trazem `secao` — e só então. Sem ela nada
 * muda: nenhum cabeçalho aparece e a lista continua plana.
 *
 * ⚠️ **O painel é `w-full` do gatilho, e a opção quebra linha em vez de esticar.**
 * Nome de exame longo ("1,25 di-hidroxivitamina D (Calcitriol - PMI)") empurrava
 * a lista para fora do diálogo; a largura é do campo, nunca do conteúdo.
 */
export function ComboBox({
  id,
  valor,
  aoEscolher,
  opcoes,
  rotuloSelecionado,
  placeholder = 'Selecione',
  placeholderBusca = 'Buscar...',
  vazio = 'Nada encontrado.',
  aoBuscar,
  carregando = false,
  desabilitado = false,
  className,
  'aria-label': ariaLabel,
  permitirLivre = false,
  rotuloLivre = ROTULO_LIVRE_PADRAO,
}: Props) {
  const [aberto, setAberto] = React.useState(false);
  const [termo, setTermo] = React.useState('');
  const [ativo, setAtivo] = React.useState(0);
  const [paraCima, setParaCima] = React.useState(false);

  const raiz = React.useRef<HTMLDivElement>(null);
  const gatilho = React.useRef<HTMLButtonElement>(null);
  const campo = React.useRef<HTMLInputElement>(null);
  const lista = React.useRef<HTMLDivElement>(null);

  const escolhida = opcoes.find((o) => o.valor === valor);
  const rotulo = escolhida?.rotulo ?? rotuloSelecionado ?? '';

  // Filtro local por cima do que o servidor devolveu: enquanto a consulta
  // adiada não volta, a lista já reage ao que está sendo digitado.
  const visiveis = React.useMemo(() => {
    // `achatar` dos dois lados: quem digita "caes" acha "cães", e quem digita
    // "CANINO" acha "Caninos". O acento do rótulo foi escolhido por quem
    // cadastrou — não dá para exigir que quem procura acerte a grafia.
    const t = achatar(termo.trim());
    if (!t) return opcoes;

    const filtradas = opcoes.filter(
      (o) => achatar(o.rotulo).includes(t) || (o.detalhe ? achatar(o.detalhe).includes(t) : false),
    );

    // A opção livre só aparece quando o que foi digitado não é EXATAMENTE uma
    // das opções: oferecer "Usar «Hemograma»" logo acima do Hemograma da lista
    // seria pedir para a pessoa criar uma duplicata sem perceber.
    if (!permitirLivre) return filtradas;
    const exata = filtradas.some((o) => achatar(o.rotulo.trim()) === t);
    if (exata) return filtradas;

    const livre = termo.trim();
    return [{ valor: livre, rotulo: rotuloLivre(livre) }, ...filtradas];
  }, [opcoes, termo, permitirLivre, rotuloLivre]);

  function fechar() {
    setAberto(false);
    setTermo('');
    aoBuscar?.('');
  }

  function abrir() {
    if (desabilitado) return;
    // Painel para cima quando não cabe abaixo: o diálogo tem rolagem própria e
    // a lista aberta no rodapé ficaria cortada.
    const caixa = gatilho.current?.getBoundingClientRect();
    setParaCima(Boolean(caixa && window.innerHeight - caixa.bottom < 280 && caixa.top > 280));
    setAtivo(0);
    setAberto(true);
  }

  function selecionar(opcao: OpcaoCombo | undefined) {
    if (!opcao || opcao.desabilitada) return;
    aoEscolher(opcao.valor);
    fechar();
    gatilho.current?.focus();
  }

  React.useEffect(() => {
    if (aberto) campo.current?.focus();
  }, [aberto]);

  // Assinatura em vez do array: quem consome pode montar `opcoes` inline, e
  // depender da referência zeraria o item ativo a cada render — a seta para
  // baixo nunca sairia do primeiro.
  const assinatura = visiveis.map((o) => o.valor).join('|');
  React.useEffect(() => {
    setAtivo(0);
  }, [assinatura]);

  React.useEffect(() => {
    if (!aberto) return;
    // `querySelectorAll` e não `children[ativo]`: com seções, a opção `ativo`
    // está DENTRO do `role="group"`, e o índice de filho direto apontaria para
    // o cabeçalho errado — ou para nada.
    lista.current?.querySelectorAll('[role="option"]')[ativo]?.scrollIntoView({ block: 'nearest' });
  }, [ativo, aberto]);

  React.useEffect(() => {
    if (!aberto) return;

    function aoApontarFora(evento: PointerEvent) {
      if (!raiz.current?.contains(evento.target as Node)) fechar();
    }

    document.addEventListener('pointerdown', aoApontarFora);
    return () => document.removeEventListener('pointerdown', aoApontarFora);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  /**
   * As opções visíveis fatiadas em blocos por `secao`, guardando o índice
   * ORIGINAL de cada uma — é ele que a navegação por teclado usa, e ele não
   * pode ser reiniciado a cada bloco.
   *
   * Bloco sem `secao` (a lista plana de sempre, ou a opção de texto livre)
   * renderiza sem cabeçalho e sem `role="group"`.
   */
  const blocos = React.useMemo(() => {
    const saida: { secao?: string; itens: { opcao: OpcaoCombo; indice: number }[] }[] = [];

    visiveis.forEach((opcao, indice) => {
      const ultimo = saida[saida.length - 1];
      if (ultimo && ultimo.secao === opcao.secao) ultimo.itens.push({ opcao, indice });
      else saida.push({ secao: opcao.secao, itens: [{ opcao, indice }] });
    });

    return saida;
  }, [visiveis]);

  function mover(passo: number) {
    if (visiveis.length === 0) return;
    setAtivo((i) => (i + passo + visiveis.length) % visiveis.length);
  }

  function aoTeclar(evento: React.KeyboardEvent) {
    switch (evento.key) {
      case 'ArrowDown':
        evento.preventDefault();
        mover(1);
        break;
      case 'ArrowUp':
        evento.preventDefault();
        mover(-1);
        break;
      case 'Home':
        evento.preventDefault();
        setAtivo(0);
        break;
      case 'End':
        evento.preventDefault();
        setAtivo(Math.max(visiveis.length - 1, 0));
        break;
      case 'Enter':
        evento.preventDefault();
        selecionar(visiveis[ativo]);
        break;
      case 'Escape':
        // Sem o stop, o Radix entende como pedido de fechar o DIÁLOGO inteiro e
        // o formulário meio preenchido some junto com a lista.
        evento.preventDefault();
        evento.stopPropagation();
        fechar();
        gatilho.current?.focus();
        break;
      case 'Tab':
        fechar();
        break;
    }
  }

  return (
    <div ref={raiz} className="relative">
      <button
        ref={gatilho}
        id={id}
        type="button"
        role="combobox"
        aria-expanded={aberto}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        disabled={desabilitado}
        onClick={() => (aberto ? fechar() : abrir())}
        onKeyDown={(e) => {
          if (!aberto && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            abrir();
          }
        }}
        className={cn(
          'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
      >
        <span className={cn('truncate text-left', !rotulo && 'text-muted-foreground')}>
          {rotulo || placeholder}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
      </button>

      {aberto && (
        <div
          className={cn(
            'absolute z-50 w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md',
            paraCima ? 'bottom-full mb-1' : 'top-full mt-1',
          )}
        >
          <div className="flex items-center gap-2 border-b px-3">
            <Search className="h-4 w-4 shrink-0 opacity-50" />
            <input
              ref={campo}
              value={termo}
              onChange={(e) => {
                setTermo(e.target.value);
                aoBuscar?.(e.target.value);
              }}
              onKeyDown={aoTeclar}
              placeholder={placeholderBusca}
              aria-label={placeholderBusca}
              className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            {carregando && <Loader2 className="h-4 w-4 shrink-0 animate-spin opacity-50" />}
          </div>

          {visiveis.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {carregando ? 'Buscando...' : vazio}
            </p>
          ) : (
            <div ref={lista} role="listbox" className="max-h-56 overflow-y-auto p-1">
              {blocos.map((bloco, ordem) => {
                const itens = bloco.itens.map(({ opcao, indice }) => (
                  <button
                    key={opcao.valor}
                    type="button"
                    role="option"
                    aria-selected={opcao.valor === valor}
                    disabled={opcao.desabilitada}
                    onMouseEnter={() => setAtivo(indice)}
                    onClick={() => selecionar(opcao)}
                    className={cn(
                      'flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-left text-sm',
                      indice === ativo && !opcao.desabilitada && 'bg-accent text-accent-foreground',
                      opcao.desabilitada && 'cursor-not-allowed opacity-50',
                    )}
                  >
                    <span className="min-w-0 flex-1 leading-snug">
                      <span className="block break-words">{opcao.rotulo}</span>
                      {opcao.detalhe && (
                        <span className="block break-words text-xs text-muted-foreground">
                          {opcao.detalhe}
                        </span>
                      )}
                    </span>
                    {(opcao.valor === valor || opcao.desabilitada) && (
                      <Check className="mt-0.5 h-4 w-4 shrink-0" />
                    )}
                  </button>
                ));

                if (!bloco.secao) {
                  return <React.Fragment key={`sem-secao-${ordem}`}>{itens}</React.Fragment>;
                }

                return (
                  // `role="group"` com `aria-label`, e não um `<div>` solto com
                  // texto: filho de `listbox` que não é `option` nem `group`
                  // não tem significado para o leitor de tela, e o cabeçalho
                  // ficaria mudo.
                  <div key={`${bloco.secao}-${ordem}`} role="group" aria-label={bloco.secao}>
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                      {bloco.secao}
                    </div>
                    {itens}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
